import "./AssetTrackingModal.scss"
import pictogramAsset from "assets/img/pictogram/asset.svg"
import IconClose from "assets/img/icon-close.svg?react"
import IconMobile from "assets/img/icon-mobile-sm.svg?react"
import IconDesktop from "assets/img/icon-desktop-sm.svg?react"

import { FC, Fragment, useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import InfiniteScroll from "react-infinite-scroller"
import cn from "classnames"

import { Coordinates, Guid } from "@/types"
import Modal from "components/Modal/Modal"
import MapView, { IMapViewProps, MapViewControl } from "components/MapView/MapView"
import AssetLocationModel from "models/AssetLocation"
import AssetService from "services/AssetService"
import usePagedData from "hooks/usePagedData"
import { Flex, FlexItem } from "components/Flex"
import useAsset from "hooks/useAsset"
import useArticle from "hooks/useArticle"
import { Grid, GridItem } from "components/Grid"
import Pictogram from "components/Pictogram/Pictogram"
import { getArticleDescriptionHeaderTextType } from "utils/utils"
import Text from "components/Text/Text"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import InfoButton from "components/InfoButton/InfoButton"
import articleSidebars from "stores/articleSidebars"
import { ArticleZts } from "models/ArticleZts"
import Lmid from "components/Lmid/Lmid"
import Button from "components/Button/Button"
import AssetLocation from "./AssetLocation"
import Spinner from "components/Spinner/Spinner"
import { MapMarkerType } from "components/MapView/MapMarker/MapMarker"
import { LocationSource } from "models/Location"
import MapMarkerTracking from "components/MapView/MapMarker/MapMarkerTracking"
import Checkbox from "components/Checkbox/Checkbox"
import { range } from "lodash"

interface IAssetTrackingModalProps {
    assetId: Guid
    onClose: () => void
}

const AssetTrackingModal: FC<IAssetTrackingModalProps> = ({
    assetId,
    onClose,
}) => {
    const { t } = useTranslation()
    const scrollEl = useRef<HTMLDivElement>(null)
    const { asset } = useAsset(assetId)
    const { article } = useArticle(asset?.articleId)
    const [selectedLocationIndexes, setSelectedLocationIndexes] = useState<number[]>([0])
    const [centerMapToCoordinates, setCenterMapToCoordinates] = useState<Coordinates | null>(null)

    const fetchLocations = useCallback(async () => {
        setCenterMapToCoordinates(null)
        return AssetService.getAssetLocations({ assetId })
    }, [assetId])

    const {
        items: locations,
        pagination: locationsPagination,
        isLoading: isLoadingLocations,
        loadNextPage: loadMoreLocations,
        hasMore: hasMoreLocations,
    } = usePagedData<AssetLocationModel>(
        fetchLocations,
    )

    const locationsSelectionState = useMemo(() => {
        if (locations) {
            if (selectedLocationIndexes.length === locations.length) {
                return "all"
            }
            if (selectedLocationIndexes.length > 0 && selectedLocationIndexes.length < locations.length) {
                return "some"
            }
        }
        return "none"
    }, [locations, selectedLocationIndexes.length])

    function toggleAllLocationsSelection() {
        if (!locations) return
        setCenterMapToCoordinates(null)
        if (locationsSelectionState === "all") {
            setSelectedLocationIndexes([])
            return
        }
        setSelectedLocationIndexes(range(0, locations.length))
    }

    function toggleLocationSelection(index: number) {
        setCenterMapToCoordinates(null)
        if (selectedLocationIndexes.includes(index)) {
            setSelectedLocationIndexes(selectedLocationIndexes.filter(i => i !== index))
        } else {
            setSelectedLocationIndexes([...selectedLocationIndexes, index])
        }
    }

    const mapMarkers: IMapViewProps['markers'] = useMemo(() => {
        if (!locations || !selectedLocationIndexes) return []
        return selectedLocationIndexes.map(index => {
            const theLocation = locations[index]
            return {
                index,
                coordinates: theLocation.location,
                type: index === 0 ? MapMarkerType.default : MapMarkerType.tracking,
                title: <Flex alignItems="center">
                    {index !== 0 &&
                        <MapMarkerTracking
                            number={locationsPagination ? locationsPagination.totalCount - index :  undefined }
                        />
                    }
                    <Flex alignItems="center" gap=".4rem"
                        className={cn(
                            "asset-tracking-modal__map-location-label",
                            `asset-tracking-modal__map-location-label--${theLocation.stateOfStorage}`,
                            { "asset-tracking-modal__map-location-label--past": index > 0 }
                        )}>
                        <FlexItem>{theLocation.locatingTime?.format(t('config.dateLongFormat'))}</FlexItem>
                        {!!theLocation.source &&
                            <FlexItem>
                                {theLocation.source === LocationSource.mobile && <IconMobile width={16} height={16} />}
                                {theLocation.source === LocationSource.web && <IconDesktop width={16} height={16} />}
                            </FlexItem>
                        }
                    </Flex>
                </Flex>,
                isClusterable: index !== 0,
            }
        })
    }, [locations, locationsPagination, selectedLocationIndexes, t])

    const isModalLoading = !locations || !asset || !article

    return (
        <Modal
            maxWidth="100%"
            fullHeight
            onClose={onClose}
            className="asset-tracking-modal"
            isBusy={isModalLoading}>
            <div className="asset-tracking-modal__title">
                {t('asset.assetTracking')}
            </div>
            {!isModalLoading &&
                <>
                    <div className="asset-tracking-modal__map">
                        <MapView
                            markers={mapMarkers}
                            autoCenterToMarkers={!centerMapToCoordinates}
                            initCenter={centerMapToCoordinates || undefined}
                            controls={[
                                MapViewControl.type,
                                MapViewControl.fullscreen,
                                MapViewControl.zoom,
                                MapViewControl.zoomToFit,
                            ]}
                        />
                    </div>
                    <Flex className="asset-tracking-modal__sidebar" flow="column">
                        <FlexItem shrink={0} className="border-bottom-grey-100 pt-3 px-3 pb-1">
                            <Grid
                                columns="6.4rem auto max-content">
                                <GridItem>
                                    <Pictogram src={pictogramAsset} />
                                </GridItem>
                                <GridItem className="ml-3">
                                    <Flex alignItems="center">
                                        <Text type={getArticleDescriptionHeaderTextType(asset.altArticleDescription || article.description)} className="mr-2">
                                            <ArticleDescription
                                                articleDescription={asset.altArticleDescription || article.description}
                                                clamp={2}
                                            />
                                        </Text>
                                        <InfoButton onClick={() => articleSidebars.show({ articleId: article.id! })} />
                                    </Flex>
                                    {asset.altArticleDescription
                                        ? <div className="color-grey-500 mb-1">
                                            {t('alternativeArticleDescription.alternativeArticleDescription')}
                                        </div>
                                        : (
                                            article instanceof ArticleZts && article.descriptionOverwritten
                                                ? <div className="color-grey-500 mb-1">
                                                    {t('article.notOriginalZtsDescription')}
                                                </div>
                                                : null
                                        )
                                    }
                                    <Grid columns="max-content minmax(0, max-content)" gap="0.8rem">
                                        <GridItem>{t('common.lmid')}:</GridItem>
                                        <GridItem>
                                            <Lmid
                                                lmid={asset.lmid}
                                                labeled={asset.isLabeled}
                                                inCollection={asset.linkedToAssetCollection}
                                            />
                                        </GridItem>
                                    </Grid>
                                    {!!asset.splitParentId && !!asset.splitParentLmid &&
                                        <Lmid
                                            lmid={asset.splitParentLmid}
                                            assetId={asset.splitParentId}
                                            isSplit
                                        />
                                    }
                                </GridItem>
                                <GridItem>
                                    <Button
                                        onClick={onClose}
                                        icon={<IconClose />}
                                        theme="transparent"
                                        round
                                        aria-label="Close"
                                        data-testid="modal-close"
                                    />
                                </GridItem>
                            </Grid>
                            <div className="mt-4">
                                <Checkbox
                                    checked={locationsSelectionState === "all"}
                                    partiallyChecked={locationsSelectionState === "some"}
                                    onChange={toggleAllLocationsSelection}
                                    label={t('common.selectAll')}
                                />
                            </div>
                        </FlexItem>
                        <FlexItem className="h-100 overflow-auto" ref={scrollEl}>
                            <InfiniteScroll
                                loadMore={loadMoreLocations}
                                hasMore={hasMoreLocations}
                                getScrollParent={() => scrollEl?.current || null}
                                useWindow={false}>
                                {locations?.map((item, idx) => (
                                    <Fragment key={idx}>
                                        {idx === 1 &&
                                            <Text type="pSmallest" className="color-grey-600 font-bold py-2 pb-half px-2 border-bottom-grey-100">
                                                {t('asset.pastLocations')}
                                            </Text>
                                        }
                                        <div className="border-grey-100-between">
                                            <AssetLocation
                                                assetLocation={item}
                                                number={locationsPagination && idx !== 0 ? locationsPagination.totalCount - idx :  undefined }
                                                selected={selectedLocationIndexes.includes(idx)}
                                                onToggleSelection={() => toggleLocationSelection(idx)}
                                                onMarkerClick={() => setCenterMapToCoordinates(item.location)}
                                            />
                                        </div>
                                    </Fragment>
                                ))}
                                {isLoadingLocations &&
                                    <Spinner size={24} className="py-3" />
                                }
                            </InfiniteScroll>
                        </FlexItem>
                    </Flex>
                </>
            }
        </Modal>

    )
}

export default AssetTrackingModal
